import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { lastValueFrom, Subscription } from 'rxjs';
import { AutocompletePrediction } from '@evermile/api/model/autocompletePrediction';
import { LocationsService, TrackingInfo, ValidationsService } from '@evermile/customer-tracking';
import { AddressType, PlaceResponse, PlaceTypesEnum } from '@evermile/api';
import { RequiredAddress } from '@evermile/api/model/requiredAddress';
import { Address } from '@evermile/customer-tracking/model/address';
import { errorMessagesMap, errorType } from '../../../services/addressUtils';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

@Component({
  selector: 'common-change-address-search-input',
  templateUrl: './change-address-search-input.component.html',
  styleUrls: ['./change-address-search-input.component.scss'],
})
export class ChangeAddressSearchInputComponent implements OnInit {
  form!: FormGroup;
  @Input() orderDetails!: TrackingInfo;
  @Input() invalid = false;
  @Input() addressSelected?: Address;
  @Input({ required: true }) formControlExternalName!: string;
  @Output() validAddressSelected: EventEmitter<Address> = new EventEmitter();
  @Output() emitInvalidAddress = new EventEmitter();
  error: string = errorType.NONE;
  errorMessage?: string;
  debounceTimeout: any;
  waitingForResults = true;
  inputBeforePostalCodeToSuggestions = '';
  isPostalCodeSearch = false;
  autoCompleteSuggestionsAddressList: any[] = [];
  debounceTimeoutDuration = 200;
  allowBack = false;
  isHandlingSelectedOption = false;
  postalCodeDrillDown = '';
  placeToken = '';
  visible = false;

  //Subs
  formSub?: Subscription;
  autoCompleteSuggestionsSub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private apiLocationsService: LocationsService,
    private apiValidationService: ValidationsService,
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      address: new FormControl(this.addressSelected?.addressLine1 || '', { nonNullable: true }),
      addressLine2: new FormControl(''),
      isValid: new FormControl(false, { nonNullable: true }),
    });
    this.formSub = this.form.controls['address'].valueChanges.subscribe((value: string) => {
      this.error = errorType.NONE;
      this.errorMessage = '';
      if (this.debounceTimeout) clearTimeout(this.debounceTimeout);
      if (!value) {
        // Clear input cache
        this.inputBeforePostalCodeToSuggestions = '';
        this.isPostalCodeSearch = false;

        // Reset all error messages
        this.showAddressError(errorType.NONE);
        this.form.controls['isValid'].setValue(false);

        // Clear Results
        this.clearLists();
      } else {
        if (value.length >= 5) {
          this.waitingForResults = true;
          const basedOnGEO = this.orderDetails?.originAddress?.geoLocation;
          this.debounceTimeout = setTimeout(() => {
            this.autoCompleteSuggestionsSub = this.apiLocationsService
              .addressSuggestionsGet(
                value,
                'Address',
                [PlaceTypesEnum.Address],
                `${basedOnGEO?.lat},${basedOnGEO?.lng}`,
                false,
              )
              .subscribe((response: any) => {
                this.clearLists();
                this.isPostalCodeSearch = false;

                // Address
                if (response.predictions && response.predictions.length) {
                  this.autoCompleteSuggestionsAddressList = response.predictions;
                  this.placeToken = response.token;
                  this.isPostalCodeSearch = this.autoCompleteSuggestionsAddressList.some(
                    (item: any) => item['kind'] && item.kind === 'LocationGroup',
                  );
                  if (this.isPostalCodeSearch) {
                    this.postalCodeDrillDown = this.form.value.address;
                  }
                }
              });
          }, this.debounceTimeoutDuration);
        } else {
          this.clearLists();
        }
      }
      this.error = 'INVALID';
      this.form.controls['isValid'].setValue(false);
    });
    setTimeout(() => {
      document.getElementById('search-field')?.focus();
      this.visible = true;
    }, 1000);
  }

  backToPostalCode(trigger: MatAutocompleteTrigger, event?: Event) {
    if (event) event.stopPropagation();
    trigger.openPanel();
    this.form.controls['address'].setValue(this.postalCodeDrillDown);
    this.autoCompleteSuggestionsAddressList = [];
    this.allowBack = false;
  }

  onSelectOption(event: any) {
    // event?.option?._element?.nativeElement?.click();
  }

  selectSuggestedAddressName(trigger: MatAutocompleteTrigger, option: AutocompletePrediction | any, event?: Event) {
    this.waitingForResults = false;
    if (this.isHandlingSelectedOption) return;
    this.isHandlingSelectedOption = true;
    if (option.kind && option.kind === 'LocationGroup') {
      if (event) event.stopPropagation();
      trigger.openPanel();
      this.autoCompleteSuggestionsAddressList = [];
      this.allowBack = true;
      this.form.controls['address'].setValue(option.description);
    } else {
      const placeId = option.placeId;
      this.apiLocationsService.addressPlaceGet(placeId, this.placeToken).subscribe((placeDetails: any) => {
        this.checkValidAddress(placeDetails);
        this.allowBack = false;
        this.inputBeforePostalCodeToSuggestions = '';
      });
    }
  }

  async checkValidAddress(inputAddress?: PlaceResponse) {
    if (!inputAddress) return;
    let addressToValidate: RequiredAddress;
    if (inputAddress) {
      addressToValidate = {
        addressLine1: inputAddress.formattedAddress,
        postcode: inputAddress.postalCode,
        city: inputAddress.city,
        geoLocation: inputAddress.geoLocation,
        country: 'UK',
      };
    }
    if (addressToValidate!.postcode) {
      if (addressToValidate!.postcode?.length >= 5 && addressToValidate!.postcode?.length <= 8) {
        const validateAddress$ = this.apiValidationService.addressesValidatePost({ addresses: [addressToValidate!] });
        try {
          await lastValueFrom(validateAddress$);
          this.showAddressError(errorType.NONE);
          this.form.get('address')?.setValue(inputAddress.formattedAddress);
          this.validAddressSelected.emit({
            addressLine1: inputAddress.formattedAddress,
            city: inputAddress.city,
            postalCode: inputAddress.postalCode,
            geoLocation: inputAddress.geoLocation,
            type: AddressType.Office,
          });
        } catch (e: any) {
          this.emitInvalidAddress.emit();
        }
      } else {
        this.emitInvalidAddress.emit();
      }
    } else {
      this.emitInvalidAddress.emit();
    }
  }

  focusAutoComplete(trigger: MatAutocompleteTrigger) {
    this.isHandlingSelectedOption = false;
    trigger.openPanel();
  }

  rememberInputText() {
    this.inputBeforePostalCodeToSuggestions = this.form.value.address;
  }

  clearLists() {
    this.autoCompleteSuggestionsAddressList = [];
    this.waitingForResults = false;
  }

  showAddressError(errorType: string) {
    this.error = errorType;
    this.errorMessage = errorMessagesMap[this.error];
  }
}
